import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useI18n } from '../contexts/I18nContext';
import { Brain, TrendingUp, Users, Target, Sparkles, Lock } from 'lucide-react';
import { Link } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AIInsightsPage = () => {
  const { user } = useAuth();
  const { t, language } = useI18n();
  const [insights, setInsights] = useState(null);
  const [loading, setLoading] = useState(false);
  const [contacts, setContacts] = useState([]);

  // Debug: Log current language and translation
  console.log('AI Insights - Current language:', language);
  console.log('AI Insights - Title translation:', t('aiInsights.title'));

  useEffect(() => {
    if (user) {
      loadContacts();
    }
  }, [user, language]);

  const loadContacts = async () => {
    try {
      const response = await axios.get(`${API}/contacts`, {
        withCredentials: true,
      });
      setContacts(response.data || []);
    } catch (error) {
      console.error('Error loading contacts:', error);
    }
  };

  const generateInsights = async () => {
    setLoading(true);
    try {
      // Simulate AI analysis
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Use real contact data for analysis
      const totalContacts = contacts.length;
      const highPriority = Math.floor(totalContacts * 0.3);
      const responseRate = Math.floor(Math.random() * 30) + 70; // Random between 70-100%
      
      // Generate lead scoring from real contacts
      const leadScoring = contacts.slice(0, 5).map((contact, index) => {
        const scores = [95, 82, 68, 75, 60];
        const statuses = ['hot', 'warm', 'warm', 'hot', 'cold'];
        const reasons = [
          t('aiInsights.recentEngagement'),
          t('aiInsights.multipleInteractions'),
          t('aiInsights.showedInterest'),
          t('aiInsights.recentEngagement'),
          t('aiInsights.multipleInteractions')
        ];
        
        return {
          name: contact.name || `Contact ${index + 1}`,
          score: scores[index] || 60,
          status: statuses[index] || 'cold',
          reason: reasons[index] || t('aiInsights.showedInterest')
        };
      });
      
      const insights = {
        totalContacts,
        highPriority,
        responseRate,
        leadScoring
      };
      
      setInsights(insights);
    } catch (error) {
      console.error('Error generating insights:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center">
          <div className="w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Lock className="w-8 h-8 text-indigo-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            {t('aiInsights.authRequired')}
          </h2>
          <p className="text-gray-600 mb-6">
            {t('aiInsights.authDescription')}
          </p>
          <Link
            to="/login"
            className="inline-flex items-center justify-center px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
          >
            {t('aiInsights.goToLogin')}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 bg-gradient-to-br from-indigo-600 to-purple-600 rounded-xl flex items-center justify-center">
              <Brain className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">
                {t('aiInsights.title')}
              </h1>
              <p className="text-gray-600">
                {t('aiInsights.subtitle')}
              </p>
            </div>
          </div>
        </div>

        {/* Action Button */}
        <div className="mb-8">
          <button
            onClick={generateInsights}
            disabled={loading}
            className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-xl hover:from-indigo-700 hover:to-purple-700 transition-all shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <>
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                {t('aiInsights.startAnalysis')}
              </>
            ) : (
              <>
                <Sparkles className="w-5 h-5" />
                {t('aiInsights.getInsights')}
              </>
            )}
          </button>
        </div>

        {/* Welcome State */}
        {!insights && !loading && (
          <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
            <div className="w-20 h-20 bg-gradient-to-br from-indigo-100 to-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Brain className="w-10 h-10 text-indigo-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              {t('aiInsights.welcome').replace('{name}', user.displayName || 'there')}
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              {t('aiInsights.description').replace('{count}', contacts.length)}
            </p>
          </div>
        )}

        {/* Insights Display */}
        {insights && (
          <div className="space-y-6">
            {/* Overview Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-white rounded-xl shadow-lg p-6">
                <div className="flex items-center justify-between mb-4">
                  <Users className="w-8 h-8 text-indigo-600" />
                  <span className="text-3xl font-bold text-gray-900">{insights.totalContacts}</span>
                </div>
                <h3 className="text-sm font-medium text-gray-600">
                  {t('aiInsights.contactsEngagement').replace('{count}', insights.totalContacts)}
                </h3>
              </div>

              <div className="bg-white rounded-xl shadow-lg p-6">
                <div className="flex items-center justify-between mb-4">
                  <Target className="w-8 h-8 text-purple-600" />
                  <span className="text-3xl font-bold text-gray-900">{insights.highPriority}</span>
                </div>
                <h3 className="text-sm font-medium text-gray-600">
                  {t('aiInsights.highPriorityContacts').replace('{count}', insights.highPriority)}
                </h3>
              </div>

              <div className="bg-white rounded-xl shadow-lg p-6">
                <div className="flex items-center justify-between mb-4">
                  <TrendingUp className="w-8 h-8 text-green-600" />
                  <span className="text-3xl font-bold text-gray-900">{insights.responseRate}%</span>
                </div>
                <h3 className="text-sm font-medium text-gray-600">
                  {t('aiInsights.responseRate').replace('{rate}', insights.responseRate)}
                </h3>
              </div>
            </div>

            {/* Business Insights */}
            <div className="bg-white rounded-2xl shadow-xl p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">
                {t('aiInsights.businessInsights')}
              </h2>
              
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">
                    {t('aiInsights.keyInsights')}
                  </h3>
                  <ul className="space-y-2">
                    <li className="flex items-start gap-2">
                      <span className="text-indigo-600 mt-1">•</span>
                      <span className="text-gray-700">
                        {t('aiInsights.highPriorityContacts').replace('{count}', insights.highPriority)}
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-indigo-600 mt-1">•</span>
                      <span className="text-gray-700">
                        {t('aiInsights.activeSectors')}
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-indigo-600 mt-1">•</span>
                      <span className="text-gray-700">
                        {t('aiInsights.responseRate').replace('{rate}', insights.responseRate)}
                      </span>
                    </li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">
                    {t('aiInsights.aiRecommendations')}
                  </h3>
                  <ul className="space-y-2">
                    <li className="flex items-start gap-2">
                      <span className="text-purple-600 mt-1">→</span>
                      <span className="text-gray-700">
                        {t('aiInsights.focusContacts').replace('{count}', insights.highPriority)}
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-purple-600 mt-1">→</span>
                      <span className="text-gray-700">
                        {t('aiInsights.scheduleFollowups')}
                      </span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Lead Scoring */}
            <div className="bg-white rounded-2xl shadow-xl p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">
                {t('aiInsights.leadScoring').replace('{count}', insights.leadScoring.length)}
              </h2>
              
              <div className="space-y-4">
                {insights.leadScoring.map((lead, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:border-indigo-300 transition-colors"
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-gradient-to-br from-indigo-100 to-purple-100 rounded-full flex items-center justify-center">
                        <span className="text-lg font-bold text-indigo-600">
                          {lead.name.charAt(0)}
                        </span>
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900">{lead.name}</h3>
                        <p className="text-sm text-gray-600">{lead.reason}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className="text-2xl font-bold text-gray-900">{lead.score}</span>
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                        lead.status === 'hot' ? 'bg-red-100 text-red-700' :
                        lead.status === 'warm' ? 'bg-orange-100 text-orange-700' :
                        'bg-blue-100 text-blue-700'
                      }`}>
                        {lead.status === 'hot' ? t('aiInsights.hot') :
                         lead.status === 'warm' ? t('aiInsights.warm') :
                         t('aiInsights.cold')}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AIInsightsPage;